import {DrawFrmThing} from "../../drawfrm/DrawFrmThing";
import {ElevationApp} from "../../ElevationApp";
import {hingeSideOrDefault} from "../frameProfile/Helpers";
import {inches, lineBlueHeavy, lineRed, panelMetalGrey} from "../../const";
import {LinearDimension} from "../frameGrid/LinearDimension";
import {drawCutMarker} from "../../shared-2d/cutHatch";

// hinge knuckle drawn straddling the leaf's hinge edge (scene units = mm).
const HINGE_WIDTH = 1/2 * inches;

// how far the overcut/undercut dimensions sit outboard of the latch edge, past the
// jamb, mirroring the hinge dimensions on the opposite edge (scene units = mm).
const CUT_DIM_CLEARANCE = 4 * inches;

// Draws a single door leaf face at the scene origin (top-left at 0,0), with the
// hinges marching down the hinge-side edge. Hardware (handle/lock) and lites are
// added as selectable children by the view, so they inherit this leaf's local
// coordinate space; the leaf just exposes the latch geometry they need.
export class DoorLeaf extends DrawFrmThing {
    private elev: ElevationApp;
    private active: boolean;

    // when set, this leaf skips drawing its hinge knuckles: the Locations view owns
    // them as selectable HingeThings instead (drawn over the leaf). Every other
    // caller draws the inert knuckles inline.
    private interactiveHinges: boolean;

    // when set and true, the leaf draws mirrored left-to-right (hinges on the opposite
    // edge) — the Locations view's back face. Read on every draw.
    private mirror: () => boolean;

    // the overcut/undercut dimensions, drawn only when this leaf opts into cuts
    // (the Locations view). Null for every other caller — those views never show
    // the door cuts. Created up front so draw() can just draw/hide them.
    private overcutDim: LinearDimension | null = null;
    private undercutDim: LinearDimension | null = null;

    constructor(elev: ElevationApp, active: boolean, opts?: {showCuts?: boolean; interactiveHinges?: boolean; mirror?: () => boolean}) {
        super();
        this.elev = elev;
        this.active = active;
        this.interactiveHinges = !!opts?.interactiveHinges;
        this.mirror = opts?.mirror ?? (() => false);

        if (opts?.showCuts) {
            this.overcutDim = new LinearDimension(this.lines, elev.draw);
            this.undercutDim = new LinearDimension(this.lines, elev.draw);
            this.track(this.overcutDim);
            this.track(this.undercutDim);
        }

        this.draw();

        // clicking the leaf face (the background behind the lites) clears the
        // current lite selection. Lite graphics are children of this.lines and
        // stopPropagation on their own tap, so a tap only reaches here when it
        // lands on empty leaf, a hinge or the handle.
        this.makeInteractive({
            onClick: () => {
                // ignore the synthetic tap fired at the end of a lite drag/resize:
                // when the press starts on a lite (or handle) and the release lands
                // on the leaf, pixi taps their common ancestor (this leaf). The
                // gesture is still in progress here, so skip the deselect.
                if (this.elev.draw.isPanSuspended()) return;

                this.elev.viewSettings.clearSelection();
            },
        });
    }

    // leaf width for the leaf this view is showing.
    leafWidth(): number {
        const door = this.elev.doorSpec;
        return this.active ? door.activeDoorWidth.get() : door.inActiveDoorWidth.get();
    }

    // leaf height (actual cut height, shared by both leaves of a pair).
    leafHeight(): number {
        return this.elev.doorSpec.actualHeight.get();
    }

    private hingesOnLeft(): boolean {
        return (hingeSideOrDefault(this.elev, this.active) === "left") !== this.mirror();
    }

    draw() {
        const g = this.lines;
        g.clear();

        const width = this.leafWidth();
        const height = this.leafHeight();
        const hingesOnLeft = this.hingesOnLeft();

        // an overcut removes material from the top of the leaf, so the drawn face
        // starts that far down from the top-of-jamb origin. Hinges stay
        // frame-referenced (offsetTop is measured from that same origin), so only
        // the face rect carries this offset.
        const yOffset = this.elev.doorSpec.overcut.get();

        // leaf face
        g.lineStyle(2, lineBlueHeavy, 1);
        g.beginFill(panelMetalGrey, 1);
        g.drawRect(0, yOffset, width, height);
        g.endFill();

        // hinges march down the hinge-side edge, centered on each knuckle.
        // offsetTop is measured from the top of the leaf. Skipped when the Locations
        // view draws them as selectable HingeThings instead.
        if (!this.interactiveHinges) {
            const hingeEdge = hingesOnLeft ? 0 : width;
            const hinges = this.elev.hardware.hinges.list;
            for (let i = 0; i < hinges.length; i++) {
                const cy = hinges[i].offsetTop.get();
                const hh = this.elev.hardware.hinges.hingeHeightAt(i);
                g.lineStyle(1, lineBlueHeavy, 0.5);
                g.beginFill(lineBlueHeavy, 0.5);
                g.drawRect(hingeEdge - HINGE_WIDTH / 2, cy - hh / 2, HINGE_WIDTH, hh);
                g.endFill();
            }
        }

        // hardware crosshairs (handle, lock, and any placed items) are drawn by
        // HardwareCrosshairThing children, added by the view — see DoorElevScene.

        this.drawCuts(width, height, yOffset, hingesOnLeft);
    }

    // draw the overcut/undercut dimensions when this leaf opts into cuts and the
    // cut is non-zero; hide them otherwise. Placed off the latch edge (opposite the
    // hinge dimensions), clearing the jamb the same way the hinge dimensions do.
    private drawCuts(width: number, height: number, yOffset: number, hingesOnLeft: boolean) {
        const door = this.elev.doorSpec;
        const fs = this.elev.frameSpec;
        const overcut = door.overcut.get();
        const undercut = door.undercut.get();

        // non-Locations views (no cut dims) don't show the trimmed regions — just a
        // red line marking the bottom of the door when there's an undercut.
        if (!this.overcutDim || !this.undercutDim) {
            if (undercut > 0) {
                const faceBottom = yOffset + height;
                this.lines.lineStyle(1, lineRed, 1);
                this.lines.moveTo(0, faceBottom);
                this.lines.lineTo(width, faceBottom);
            }
            return;
        }

        const latchEdge = hingesOnLeft ? width : 0;
        const dir = hingesOnLeft ? 1 : -1;
        const cross = latchEdge + dir * (fs.frameJambToDoorGap.get() + fs.jambFace.get() + CUT_DIM_CLEARANCE);

        // overcut: material removed above the leaf face (the top-of-jamb origin down
        // to the face top at yOffset).
        if (overcut > 0) {
            drawCutMarker(this.lines, 0, 0, width, yOffset);
            this.overcutDim.draw({
                axis: "vertical",
                from: 0, to: yOffset, cross,
                value: overcut,
                modified: door.overcut.isModified(),
                onSet: v => door.overcut.userSet(v, {debug: "doorSpec.overcut"}),
                onClear: () => {
                    door.overcut.set(0, {debug: "doorSpec.overcut.clear"});
                    door.overcut.userClearModified();
                },
            });
        } else {
            this.overcutDim.hide();
        }

        // undercut: material removed below the leaf face.
        if (undercut > 0) {
            const faceBottom = yOffset + height;
            drawCutMarker(this.lines, 0, faceBottom, width, undercut);
            this.undercutDim.draw({
                axis: "vertical",
                from: faceBottom, to: faceBottom + undercut, cross,
                value: undercut,
                modified: door.undercut.isModified(),
                onSet: v => door.undercut.userSet(v, {debug: "doorSpec.undercut"}),
                onClear: () => {
                    door.undercut.set(0, {debug: "doorSpec.undercut.clear"});
                    door.undercut.userClearModified();
                },
            });
        } else {
            this.undercutDim.hide();
        }
    }
}
